<template>
  <div class="w-full max-w-full min-w-0 overflow-x-hidden" dir="rtl">
    <div v-if="user.user_type === 'ma' || user.is_superuser">
      <!-- Header -->
      <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mb-4 w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
            <div class="min-w-0 overflow-hidden">
              <h3 class="text-sm font-bold text-gray-100 truncate">دقتِ سود فروش آنلاین</h3>
              <p class="text-[11px] text-gray-400 mt-1 truncate">
                این محصولات هنوز بهای دقیقِ لحظه‌ی فروششون ذخیره نشده و سودشون تخمینیه — این صفحه نشون میده چقدر ممکنه اشتباه باشه
              </p>
            </div>

            <button
                @click="fetchData"
                :disabled="loading"
                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              بروزرسانی
            </button>
          </div>
        </header>

        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="rounded-xl bg-blue-600/10 border border-blue-500/20 px-4 py-3 text-[11px] text-blue-200 leading-6">
            <i class="fas fa-info-circle ml-1"></i>
            از امروز به بعد، فروش‌های آنلاینِ جدید بهای دقیق‌شون رو ذخیره می‌کنن و دیگه تخمینی نیستن.
            برای فروش‌های قدیمی‌تر، این گزارش بر اساس تاریخچه‌ی تغییرات بهای محصول (فقط از {{ coverageStart }} به بعد ثبت شده) یک «بازه‌ی سود» نشون می‌ده،
            نه یک عدد قطعی — چون بهای دقیق روز فروش قابل بازسازی نیست.
          </div>
        </section>

        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="grid grid-cols-2 gap-2 sm:gap-3 min-w-0">
            <div class="col-span-2 lg:col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">جستجو با بارکد / GTIN</label>
              <input
                  type="text"
                  v-model="gtin"
                  placeholder="مثلاً 6221234567890"
                  dir="ltr"
                  class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div class="col-span-2 lg:col-span-1 flex items-end gap-2 min-w-0">
              <button @click="applyFilter" class="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs text-white transition shrink-0">
                اعمال فیلتر
              </button>
              <button v-if="gtin" @click="clearFilter" class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition shrink-0">
                پاک کردن
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- Loading / error -->
      <div v-if="loading" class="p-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-gray-400 mt-3 text-sm">در حال دریافت اطلاعات...</p>
      </div>

      <div v-else-if="error" class="p-10 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
        <p class="text-red-400 mt-3 text-sm">{{ error }}</p>
        <button @click="fetchData" class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition">
          تلاش مجدد
        </button>
      </div>

      <!-- Product list -->
      <div v-else class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700">
          <h4 class="text-xs font-bold text-gray-200">محصولات با سودِ تخمینی ({{ formatNumber(products.length) }})</h4>
        </header>

        <div class="p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
          <article
              v-for="p in products"
              :key="p.product_id"
              class="rounded-2xl bg-gray-900/45 border border-gray-700/80 p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden"
          >
            <div class="flex items-start justify-between gap-2 min-w-0 cursor-pointer" @click="toggleExpand(p.product_id)">
              <div class="flex items-start gap-2 min-w-0">
                <div
                    class="shrink-0 w-10 h-10 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                    :class="imgOk(p.image) ? 'cursor-zoom-in' : ''"
                    @click.stop="zoomImage(p.image)"
                >
                  <img v-if="imgOk(p.image)" :src="img_source(p.image)" class="w-full h-full object-cover" @error="markBroken(p.image)">
                  <i v-else class="fas fa-image text-gray-600 text-sm"></i>
                </div>
                <div class="min-w-0">
                  <p class="text-xs font-bold text-gray-100 leading-5 line-clamp-2 break-safe">{{ p.product_name }}</p>
                  <p class="text-[10px] text-gray-500 mt-1">{{ formatNumber(p.online_quantity_estimated) }} عدد فروش تخمینی</p>
                </div>
              </div>
              <i class="fas shrink-0 text-gray-500 mt-1" :class="expanded[p.product_id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>

            <div class="grid grid-cols-2 gap-2 mt-3 min-w-0 text-[11px]">
              <div class="rounded-xl bg-gray-800/60 border border-gray-700/60 px-3 py-2 min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">سودِ نمایش‌داده‌شده الان</p>
                <p class="text-gray-200 font-bold mt-0.5 truncate" dir="ltr">{{ formatMoney(p.current_estimated_profit) }}</p>
              </div>
              <div
                  class="rounded-xl px-3 py-2 min-w-0 overflow-hidden border"
                  :class="p.has_recorded_change && p.min_price_seen !== p.max_price_seen ? 'bg-amber-600/10 border-amber-500/20' : 'bg-gray-800/60 border-gray-700/60'"
              >
                <p class="truncate" :class="p.has_recorded_change && p.min_price_seen !== p.max_price_seen ? 'text-amber-300/80' : 'text-gray-500'">بازه‌ی ممکنِ سود</p>
                <p class="font-bold mt-0.5 truncate" dir="ltr" :class="p.has_recorded_change && p.min_price_seen !== p.max_price_seen ? 'text-amber-300' : 'text-gray-300'">
                  <template v-if="p.profit_range_low === p.profit_range_high">{{ formatMoney(p.profit_range_low) }}</template>
                  <template v-else>{{ formatMoney(p.profit_range_low) }} تا {{ formatMoney(p.profit_range_high) }}</template>
                </p>
              </div>
            </div>

            <p class="text-[10px] mt-2" :class="p.has_recorded_change ? 'text-amber-400/80' : 'text-gray-500'">
              <i class="fas" :class="p.has_recorded_change ? 'fa-exclamation-triangle' : 'fa-check-circle'"></i>
              {{ p.has_recorded_change
                ? `${formatNumber(p.change_count)} بار تغییر بها از ${coverageStart} به بعد ثبت شده`
                : `از ${coverageStart} به بعد تغییری برای این محصول ثبت نشده` }}
            </p>

            <!-- Expanded price history -->
            <div v-if="expanded[p.product_id] && p.price_history.length" class="mt-3 pt-3 border-t border-gray-700/70 space-y-2">
              <div v-for="(h, idx) in p.price_history" :key="idx" class="flex items-center justify-between gap-2 text-[10px]">
                <span class="text-gray-500">{{ h.date | toJalali('jYYYY/jMM/jDD HH:mm') }}</span>
                <span class="text-gray-400" dir="ltr">
                  {{ h.old_value !== null ? formatMoney(h.old_value) : '—' }}
                  <i class="fas fa-arrow-left mx-1 text-gray-600"></i>
                  {{ h.new_value !== null ? formatMoney(h.new_value) : '—' }}
                </span>
              </div>
            </div>
          </article>

          <div v-if="products.length === 0" class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700">
            <i class="fas fa-check-circle text-3xl text-emerald-500 mb-3 block"></i>
            <p class="text-gray-400 text-sm">همه‌ی فروش‌های آنلاین این شریک الان بهای دقیق دارن — چیزی تخمینی نیست</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="w-full rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-5 text-right">
      <h3 class="text-sm font-bold text-red-100">دسترسی غیرمجاز</h3>
      <p class="mt-2 text-xs text-red-200">دسترسی برای شما مجاز نیست.</p>
    </div>

    <!-- Image zoom overlay -->
    <div
        v-if="zoomedImage"
        @click="zoomedImage = null"
        class="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
    >
      <img :src="zoomedImage" class="max-w-full max-h-full rounded-2xl shadow-2xl object-contain" @click.stop>
      <button
          @click="zoomedImage = null"
          class="absolute top-4 left-4 sm:top-6 sm:left-6 w-9 h-9 rounded-full bg-gray-900/80 border border-gray-600 text-gray-200 hover:bg-gray-800 transition flex items-center justify-center text-lg"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import getPartnerCalculationsApi from './getPartnerCalculationsApi'

export default {
  name: 'PartnerCalculationsCostExposure',

  mixins: [getPartnerCalculationsApi],

  props: {
    partnerId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      error: null,

      gtin: '',

      coverageStart: '',
      products: [],
      expanded: {},

      zoomedImage: null,
      brokenImages: {},
    }
  },

  methods: {
    getParams() {
      const params = {}
      if (this.gtin) params.gtin = this.gtin
      return params
    },

    fetchData() {
      this.loading = true
      this.error = null

      const onSuccess = data => {
        this.coverageStart = data.audit_log_coverage_start || ''
        this.products = data.products || []
        this.loading = false
      }
      const onError = () => {
        this.error = 'خطا در دریافت اطلاعات'
        this.loading = false
      }

      if (this.partnerId) {
        this.getAdminPartnerCalculationsCostExposure(this.partnerId, this.getParams(), onSuccess, onError)
      } else {
        this.getPartnerCalculationsCostExposure(this.getParams(), onSuccess, onError)
      }
    },

    applyFilter() {
      this.fetchData()
    },

    clearFilter() {
      this.gtin = ''
      this.fetchData()
    },

    toggleExpand(productId) {
      this.$set(this.expanded, productId, !this.expanded[productId])
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'
      const number = Number(value)
      if (Number.isNaN(number)) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    // اگه عکس واقعاً لود نشه (فایل حذف‌شده/لینک خراب)، v-if صرفاً وجود URL رو چک می‌کنه
    // نه لود شدنش -- برای همین با @error این URL رو به‌عنوان خراب علامت می‌زنیم تا آیکون
    // جایگزین نشون داده بشه، نه یک باکس خالی.
    imgOk(url) {
      return !!url && !this.brokenImages[url]
    },

    markBroken(url) {
      this.$set(this.brokenImages, url, true)
    },

    zoomImage(url) {
      if (this.imgOk(url)) this.zoomedImage = this.img_source(url)
    },
  },

  watch: {
    partnerId() {
      this.fetchData()
    },
  },

  mounted() {
    this.fetchData()
  },
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.break-safe {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.min-w-0 {
  min-width: 0 !important;
}
</style>
